import type { CSSProperties } from 'react';
import styles from './Orb.module.css';

const STAGE = 28;
const SIZE = 20;
const N = 3;
const PITCH = 6;

export type OrbVariant = 'S3' | 'S4';

const RING: [number, number][] = (() => {
  const ring: [number, number][] = [];
  for (let x = 0; x < N; x++) ring.push([x, 0]);
  for (let y = 1; y < N; y++) ring.push([N - 1, y]);
  for (let x = N - 2; x >= 0; x--) ring.push([x, N - 1]);
  for (let y = N - 2; y >= 1; y--) ring.push([0, y]);
  return ring;
})();

const RING_INDEX = new Map(RING.map(([x, y], i) => [`${x},${y}`, i]));

function cellDelay(variant: OrbVariant, x: number, y: number): number {
  if (variant === 'S4') {
    return (x / (N - 1)) * 1100;
  }
  const i = RING_INDEX.get(`${x},${y}`);
  if (i === undefined) return 0;
  return -(((RING.length - i) % RING.length) / RING.length) * 1700;
}

function latticeCells(variant: OrbVariant) {
  const cells: Array<{
    key: string;
    left: number;
    top: number;
    delay: number;
    still: boolean;
  }> = [];
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      cells.push({
        key: `${x},${y}`,
        left: x * PITCH,
        top: y * PITCH,
        delay: cellDelay(variant, x, y),
        still: variant === 'S3' && !RING_INDEX.has(`${x},${y}`),
      });
    }
  }
  return cells;
}

export function orbVariantForTool(toolName: string): OrbVariant {
  const name = toolName.toLowerCase();
  if (
    name.includes('search') ||
    name.includes('find') ||
    name.includes('list')
  ) {
    return 'S4';
  }
  return 'S3';
}

export function Orb({
  variant = 'S3',
  size = SIZE,
}: {
  variant?: OrbVariant;
  size?: number;
}) {
  return (
    <span className={styles.root}>
      <span
        className={styles.glyph}
        aria-hidden
        style={
          {
            width: size,
            height: size,
            '--orb-k': size / STAGE,
          } as CSSProperties
        }
      >
        <span className={styles.lattice} data-variant={variant}>
          {latticeCells(variant).map((cell) => (
            <span
              key={cell.key}
              className={styles.cell}
              data-still={cell.still ? '' : undefined}
              style={{
                left: cell.left,
                top: cell.top,
                animationDelay: `${cell.delay}ms`,
              }}
            />
          ))}
        </span>
      </span>
    </span>
  );
}
